import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import MultiSelectDropdown from 'components/ReusableComponents/MultiSelectDropdown'
import Table, { TitleCell, TitleRow } from 'components/ReusableComponents/Table'
import Tab from 'components/ReusableComponents/Tabs'
import useSyncedState from 'hooks/useSyncedState'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import { Link } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'

import { HotelCard } from './HotelCard/HotelCard'
import styles from './Hotels.module.scss'
import {
  filterByChainAndParentChain,
  filterByGroupAndChain,
  filterByGroupAndParentChain
} from './sortHelpers'
import { useHotelsData } from './useData'

const sortableIndexes = [
  'hotel',
  'group',
  'brand',
  'csm',
  'addedOn',
  'updatedOn',
  'status'
]

const sortableIndexesWithServiceStartDate = [
  'hotel',
  'group',
  'brand',
  'csm',
  'addedOn',
  'serviceStartDate',
  'status'
]

const hotelStatusByType = {
  onboarding: {
    PendingCampaignBrandRegistration: 'Pending campaign reg.',
    PendingCampaignBrandRegistrationDeclined: 'Pending campaign reg. declined',
    PendingPhoneNumberSetup: 'Pending telecom setup',
    PendingCampaignProviderImport: 'Pending campaign import',
    HotelConfigurationStage: 'Hotel configuration stage'
  },
  upAndRunning: {
    BasicAccount: 'Basic account',
    MediumAccount: 'Medium account',
    LargeAccount: 'Large account',
    AboutToLose: 'About to lose'
  },
  needsAttention: {
    PendingCampaignBrandRegistration: 'Pending campaign reg.',
    PendingPhoneNumberSetup: 'Pending telecom setup',
    PendingCampaignProviderImport: 'Pending campaign import',
    HotelConfigurationStage: 'Hotel Configuration Stage',
    BasicAccount: 'Basic account',
    MediumAccount: 'Medium account',
    LargeAccount: 'Large account',
    AboutToLose: 'About to lose'
  },
  churned: {
    Churned: 'Churned'
  },
  paused: {
    Paused: 'Paused'
  },
  followUp: {},
  disconnected: {},
  deleted: {},
  // API-supported list type (e.g. /hotels?type=allActive); no status subset in this view
  allActive: {}
}

const Hotels = () => {
  const [type] = useSyncedState('type', 'hotelsType', 'onboarding')
  const isHealtyHotel = type === 'upAndRunning'
  const isOnboarding = type === 'onboarding'
  const sortIndexes = isOnboarding ? sortableIndexesWithServiceStartDate : sortableIndexes
  const usesStatusIssueColumn = !isOnboarding && !isHealtyHotel

  const {
    sortedAndFilteredHotels,
    hotels,
    csmUsers,
    isLoading,
    sort: { sortParam, setSortParam, sortDirection, setSortDirection },
    filter: {
      selectedParentChain,
      setSelectedParentChain,
      selectedChain,
      setSelectedChain,
      selectedGroup,
      setSelectedGroup,
      selectedStatus,
      setSelectedStatus,
      selectedCsmIds,
      setSelectedCsmIds,
      clearFilters
    },
    search: { searchQuery, setSearchQuery },
    refetchData
  } = useHotelsData(type)

  function handleTitleClick(i) {
    const statusColumnIndex = sortIndexes.indexOf('status')

    if (usesStatusIssueColumn && i === statusColumnIndex) {
      if (sortParam !== 'status' && sortParam !== 'issue') {
        setSortParam('status')
        setSortDirection('down')
      } else if (sortParam === 'status' && sortDirection === 'down') {
        setSortParam('issue')
        setSortDirection('down')
      } else if (sortParam === 'issue' && sortDirection === 'down') {
        setSortParam('status')
        setSortDirection('up')
      } else if (sortParam === 'status' && sortDirection === 'up') {
        setSortParam('issue')
        setSortDirection('up')
      } else if (sortParam === 'issue' && sortDirection === 'up') {
        setSortParam('status')
        setSortDirection('down')
      } else {
        setSortParam('status')
        setSortDirection('down')
      }
      return
    }

    if (sortParam === sortIndexes[i]) {
      setSortDirection(d => (d === 'up' ? 'down' : 'up'))
    }

    if (sortIndexes[i]) {
      setSortParam(sortIndexes[i])
    }
  }

  const sortIndex =
    usesStatusIssueColumn && (sortParam === 'status' || sortParam === 'issue')
      ? sortIndexes.indexOf('status')
      : sortIndexes.indexOf(sortParam)

  const hotelStatusOptions = hotelStatusByType[type] ?? {}

  const filteredForGroupName = filterByChainAndParentChain(
    hotels,
    selectedChain,
    selectedParentChain,
    selectedStatus
  )

  const filteredForChain = filterByGroupAndParentChain(
    hotels,
    selectedParentChain,
    selectedGroup,
    selectedStatus
  )

  const filteredForParentChain = filterByGroupAndChain(
    hotels,
    selectedChain,
    selectedGroup,
    selectedStatus
  )

  return (
    <div className={styles.wrapper}>
      <ReactTooltip id="hotels-tooltip" place="top" type="dark" effect="solid" />

      <CustomHeader title={`Hotels (${hotels.length})`} isFixed width={1100}>
        <div className={styles.helpText}>
          <InfoIcon /> Add new hotels from <Link to="/contracts"> contracts screen.</Link>
        </div>
      </CustomHeader>

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <ReactTooltip />
          <Table
            widths={[245, 160, 120, 40, 90, 90, 170, 165]}
            sortIndex={sortIndex}
            sortDirection={sortDirection}
            handleTitleCellClick={handleTitleClick}
          >
            <div className={styles.headerWrapper}>
              <Tab.Parent>
                <Tab exact to="/hotels?type=onboarding">
                  Onboarding {type === 'onboarding' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=upAndRunning">
                  Up and Running{' '}
                  {type === 'upAndRunning' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=needsAttention">
                  Needs Attention{' '}
                  {type === 'needsAttention' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=churned">
                  Churned
                  {type === 'churned' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=paused">
                  Paused
                  {type === 'paused' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=disconnected">
                  Disconnected{' '}
                  {type === 'disconnected' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=followUp">
                  Remind later {type === 'followUp' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=deleted">
                  Deleted {type === 'deleted' && !isLoading && `(${hotels.length})`}
                </Tab>
              </Tab.Parent>
              <div className={styles.header}>
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All groups',
                    ...(selectedGroup === 'all'
                      ? {}
                      : { [selectedGroup]: selectedGroup }),
                    ...filteredForGroupName.reduce((prev, curr) => {
                      return {
                        ...prev,
                        [curr.merchantName]:
                          curr.merchantName === 'Independently Managed Hotels'
                            ? '- Independently Managed'
                            : curr.merchantName
                      }
                    }, {})
                  }}
                  value={selectedGroup}
                  onChange={e => setSelectedGroup(e.target.value)}
                />
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All brands',
                    ...(selectedParentChain === 'all'
                      ? {}
                      : {
                          [selectedParentChain]: parentChainNames[selectedParentChain]
                        }),
                    ...filteredForParentChain.reduce(
                      (prev, curr) => ({
                        ...prev,
                        [curr.parentChainCode]: parentChainNames[curr.parentChainCode]
                      }),
                      {}
                    )
                  }}
                  value={selectedParentChain}
                  onChange={e => setSelectedParentChain(e.target.value)}
                />
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All chains',
                    ...(selectedChain === 'all'
                      ? {}
                      : {
                          [selectedChain]:
                            selectedChain === 'i' ? 'independent' : selectedChain
                        }),
                    ...filteredForChain.reduce(
                      (prev, curr) => ({
                        ...prev,
                        [curr.chainCode]:
                          curr.chainCode === 'i' ? 'independent' : curr.chainCode
                      }),
                      {}
                    )
                  }}
                  value={selectedChain}
                  onChange={e => setSelectedChain(e.target.value)}
                />
                {Object.entries(hotelStatusOptions).length > 0 && (
                  <MultiSelectDropdown
                    options={hotelStatusOptions}
                    value={selectedStatus}
                    itemName="statuses"
                    onChangeValue={setSelectedStatus}
                    className={styles.statusFilter}
                    hasUnselectButton
                  />
                )}
                <MultiSelectDropdown
                  options={Object.fromEntries(
                    csmUsers.map(csm => [csm.id, csm.fullName])
                  )}
                  value={selectedCsmIds}
                  className={styles.statusFilter}
                  onChangeValue={value => setSelectedCsmIds(value)}
                  hasUnselectButton
                  itemName="CSM"
                  itemNamePlural="CSMs"
                />
                <input
                  autoFocus={true}
                  type="search"
                  className={styles.searchFilter}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Type anything to filter"
                />
                <button onClick={() => clearFilters()} className={styles.clearFilters}>
                  Clear filters{' '}
                </button>
              </div>
              <TitleRow>
                <TitleCell>HOTEL</TitleCell>
                <TitleCell>GROUP</TitleCell>
                <TitleCell>BRAND</TitleCell>
                <TitleCell>CSM</TitleCell>
                <TitleCell>ADDED ON</TitleCell>
                <TitleCell>{isOnboarding ? 'START DATE' : 'UPDATED ON'}</TitleCell>
                <TitleCell>
                  {isHealtyHotel ? 'TAGS' : isOnboarding ? 'STATUS' : 'STATUS / ISSUE'}
                </TitleCell>
                <TitleCell></TitleCell>
              </TitleRow>
            </div>

            {sortedAndFilteredHotels.map((hotel, index) => (
              <HotelCard
                index={sortedAndFilteredHotels.length - index}
                key={hotel.id}
                hotel={hotel}
                isHealthyHotel={isHealtyHotel}
                isOnboarding={isOnboarding}
                category={type}
                refetchData={refetchData}
              />
            ))}
          </Table>
        </>
      )}
    </div>
  )
}

export default Hotels
